// hooks/use-tutorial-analytics.ts
// Hook for tracking tutorial analytics events
"use client";

import { useCallback, useEffect, useRef } from "react";

import {
  trackSectionComplete,
  trackSectionView,
  trackTimeSpent,
  trackTutorialComplete,
  trackTutorialNavigation,
  trackTutorialStart,
} from "@vllnt/analytics";
import { useParams } from "next/navigation";

export type UseTutorialAnalyticsOptions = {
  /** Total number of sections */
  totalSections: number;
  /** Unique tutorial identifier (contentId) */
  tutorialId: string;
  /** Human-readable tutorial title */
  tutorialTitle: string;
};

export type UseTutorialAnalyticsReturn = {
  /** Start tracking time for a section (returns stop function) */
  startTimeTracking: (sectionId: string) => () => void;
  /** Track when user completes the entire tutorial */
  trackComplete: (completionPercent: number) => void;
  /** Track navigation between sections */
  trackNavigation: (
    sectionId: string,
    navigationType: "keyboard" | "next" | "prev" | "toc",
  ) => void;
  /** Track when user completes a section */
  trackSectionCompleted: (
    sectionId: string,
    sectionIndex: number,
    completionPercent: number,
  ) => void;
  /** Track when user views a section */
  trackSectionViewed: (sectionId: string, sectionIndex: number) => void;
  /** Track when user starts the tutorial */
  trackStart: () => void;
};

/**
 * Hook for tracking tutorial analytics events
 *
 * Integrates with @vllnt/analytics to track:
 * - Tutorial start
 * - Section views
 * - Section completions
 * - Tutorial completion
 * - Navigation patterns
 * - Time spent per section
 */
export function useTutorialAnalytics({
  totalSections,
  tutorialId,
  tutorialTitle,
}: UseTutorialAnalyticsOptions): UseTutorialAnalyticsReturn {
  const parameters = useParams();
  const locale = (parameters?.locale as string) || "en";

  // Track if we've sent the start event
  const hasTrackedStart = useRef(false);

  // Base properties for all events
  const baseProps = useCallback(
    () => ({
      locale,
      tutorial_id: tutorialId,
      tutorial_title: tutorialTitle,
    }),
    [tutorialId, tutorialTitle, locale],
  );

  const trackStart = useCallback(() => {
    if (hasTrackedStart.current) return;
    hasTrackedStart.current = true;

    trackTutorialStart({
      ...baseProps(),
      total_sections: totalSections,
    });
  }, [baseProps, totalSections]);

  const trackSectionViewed = useCallback(
    (sectionId: string, sectionIndex: number) => {
      trackSectionView({
        ...baseProps(),
        section_id: sectionId,
        section_index: sectionIndex,
        total_sections: totalSections,
      });
    },
    [baseProps, totalSections],
  );

  const trackSectionCompleted = useCallback(
    (sectionId: string, sectionIndex: number, completionPercent: number) => {
      trackSectionComplete({
        ...baseProps(),
        completion_percent: completionPercent,
        section_id: sectionId,
        section_index: sectionIndex,
      });
    },
    [baseProps],
  );

  const trackComplete = useCallback(
    (completionPercent: number) => {
      trackTutorialComplete({
        ...baseProps(),
        completion_percent: completionPercent,
        total_sections: totalSections,
      });
    },
    [baseProps, totalSections],
  );

  const trackNavigation = useCallback(
    (
      sectionId: string,
      navigationType: "keyboard" | "next" | "prev" | "toc",
    ) => {
      trackTutorialNavigation({
        ...baseProps(),
        navigation_type: navigationType,
        section_id: sectionId,
      });
    },
    [baseProps],
  );

  const startTimeTracking = useCallback(
    (sectionId: string) => {
      const startTime = Date.now();

      return () => {
        const endTime = Date.now();
        const timeSpentSeconds = Math.round((endTime - startTime) / 1000);

        // Only track if spent more than 1 second
        if (timeSpentSeconds > 1) {
          trackTimeSpent({
            ...baseProps(),
            section_id: sectionId,
            time_spent_seconds: timeSpentSeconds,
          });
        }
      };
    },
    [baseProps],
  );

  // Reset start tracking when tutorial changes
  useEffect(() => {
    hasTrackedStart.current = false;
  }, [tutorialId]);

  return {
    startTimeTracking,
    trackComplete,
    trackNavigation,
    trackSectionCompleted,
    trackSectionViewed,
    trackStart,
  };
}
